import { offlineDb, type PendingAction } from './offline-db';
import { api } from './api';

const MAX_RETRIES = 5;
const GPS_BATCH_SIZE = 50;

type ActionHandler = (payload: Record<string, unknown>) => Promise<void>;

const ACTION_HANDLERS: Readonly<Record<PendingAction['action'], ActionHandler>> = {
  start_trip: async (payload) => {
    const response = await api.post('/trips/start', payload);
    if (!response.success) throw new Error(response.error ?? 'Failed to start trip');
  },
  stop_trip: async (payload) => {
    const response = await api.post('/trips/stop', payload);
    if (!response.success) throw new Error(response.error ?? 'Failed to stop trip');
  },
  manual_trip: async (payload) => {
    const response = await api.post('/trips/manual', payload);
    if (!response.success) throw new Error(response.error ?? 'Failed to create manual trip');
  },
  gps_ping: async (payload) => {
    await api.post('/trips/gps-ping', payload);
  },
  approve_trip: async (payload) => {
    const { tripId, ...rest } = payload;
    const response = await api.post(`/approvals/${tripId}/approve`, rest);
    if (!response.success) throw new Error(response.error ?? 'Failed to approve trip');
  },
  reject_trip: async (payload) => {
    const { tripId, ...rest } = payload;
    const response = await api.post(`/approvals/${tripId}/reject`, rest);
    if (!response.success) throw new Error(response.error ?? 'Failed to reject trip');
  },
};

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
}

async function processAction(action: PendingAction): Promise<boolean> {
  const handler = ACTION_HANDLERS[action.action];
  if (!handler) {
    // Unknown action type — remove it to avoid blocking the queue
    if (action.id !== undefined) await offlineDb.pendingActions.delete(action.id);
    return true;
  }

  try {
    await handler(action.payload);
    if (action.id !== undefined) await offlineDb.pendingActions.delete(action.id);
    return true;
  } catch (error) {
    // If it's a network error, stop processing — we're still offline
    if (isNetworkError(error)) return false;

    const nextRetry = (action.retryCount ?? 0) + 1;
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (action.id !== undefined) {
      if (nextRetry >= MAX_RETRIES) {
        // Max retries exceeded — remove the action
        await offlineDb.pendingActions.delete(action.id);
      } else {
        await offlineDb.pendingActions.update(action.id, {
          retryCount: nextRetry,
          lastError: errorMessage,
        });
      }
    }
    return true; // Continue processing next action
  }
}

async function syncGpsPoints(): Promise<void> {
  const unsyncedPoints = await offlineDb.cachedGpsPoints
    .where('synced')
    .equals(0) // Dexie stores booleans as 0/1 in indexed fields
    .toArray();

  if (unsyncedPoints.length === 0) return;

  // Group by tripId
  const grouped: Record<string, typeof unsyncedPoints> = {};
  for (const point of unsyncedPoints) {
    const existing = grouped[point.tripId] ?? [];
    grouped[point.tripId] = [...existing, point];
  }

  for (const [tripId, points] of Object.entries(grouped)) {
    // Send in batches
    for (let i = 0; i < points.length; i += GPS_BATCH_SIZE) {
      const batch = points.slice(i, i + GPS_BATCH_SIZE);
      try {
        // Send each point as a ping (the API expects individual pings)
        for (const point of batch) {
          await api.post('/trips/gps-ping', {
            tripId,
            lat: point.lat,
            lng: point.lng,
            accuracy: point.accuracy,
            timestamp: point.timestamp,
          });

          // Mark as synced
          if (point.id !== undefined) {
            await offlineDb.cachedGpsPoints.update(point.id, { synced: true });
          }
        }
      } catch (error) {
        if (isNetworkError(error)) return; // Still offline, stop
        // API error — skip this batch and continue
      }
    }
  }

  // Clean up synced points older than 1 hour
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
  await offlineDb.cachedGpsPoints
    .where('synced')
    .equals(1)
    .filter((p) => p.timestamp < oneHourAgo)
    .delete();
}

export async function syncAll(): Promise<void> {
  if (!navigator.onLine) return;

  // Process pending actions in FIFO order
  const actions = await offlineDb.pendingActions.orderBy('createdAt').toArray();

  for (const action of actions) {
    const shouldContinue = await processAction(action);
    if (!shouldContinue) break; // Network went down
  }

  // Sync cached GPS points
  await syncGpsPoints();
}

export async function getPendingCount(): Promise<number> {
  const actionCount = await offlineDb.pendingActions.count();
  const gpsCount = await offlineDb.cachedGpsPoints.where('synced').equals(0).count();
  return actionCount + gpsCount;
}
